import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  IconButton,
  Checkbox,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  useTheme,
  Tooltip,
  ButtonBase
} from '@mui/material';
import { getTestCaseSourceLabel, getTestCaseStoryLabel } from './testCasesPresentation';
import { Snackbar, Alert } from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import aiLogger from '../utils/aiLogger';
import API_CONFIG from '../config/api';

const decodeHtmlEntities = (value) => {
  const input = String(value ?? '');
  if (!input || typeof document === 'undefined') return input;
  const textarea = document.createElement('textarea');
  let decoded = input;
  for (let i = 0; i < 2; i += 1) {
    textarea.innerHTML = decoded;
    const next = textarea.value;
    if (next === decoded) break;
    decoded = next;
  }
  return decoded.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\u00a0/g, ' ').trim();
};

const formatDocumentText = (value) =>
  decodeHtmlEntities(value)
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const formatSectionLabel = (key) =>
  String(key || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const TestCasesTable = ({ testCases = [], totalCount = 0, showApprovalWorkflow = false, onStatusChange = null, mode = 'tests', onRefresh = null, selectedProject = null, onOpenUpload = null, onDeleteSelected = null, onApproveSelected = null, onShowLastImport = null }) => {
  const theme = useTheme();

  const getSourceColor = (source) => {
    switch (source) {
      case 'ui_exploration':
      case 'enhanced_exploration':
        return 'primary';
      case 'prd_generation':
        return 'secondary';
      case 'ai_generation':
      case 'AI_GENERATED':
        return 'success';
      case 'manual':
      case 'uploaded':
      case 'excel_upload':
      case 'csv_upload':
        return 'success';
      case 'jira':
        return 'warning';
      case 'confluence':
        return 'info';
      case 'regression_completion':
        return 'info';
      default:
        return 'default';
    }
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [filters, setFilters] = useState({
    priority: '',
    status: '',
    module: '',
    testType: '',
    tags: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [sourceRefsDialogOpen, setSourceRefsDialogOpen] = useState(false);
  const [resolvedSourceRefs, setResolvedSourceRefs] = useState([]);
  const [sourceRefsLoading, setSourceRefsLoading] = useState(false);
  const [sourceDetailDialogOpen, setSourceDetailDialogOpen] = useState(false);
  const [sourceDetailLoading, setSourceDetailLoading] = useState(false);
  const [selectedSourceRef, setSelectedSourceRef] = useState(null);
  const [sourceDetail, setSourceDetail] = useState(null);
  const [runFilter, setRunFilter] = useState('all');

  const normalizeSourceRefs = (testCase) => {
    if (Array.isArray(testCase.source_refs)) return testCase.source_refs.filter(Boolean);
    if (Array.isArray(testCase.meta?.source_refs)) return testCase.meta.source_refs.filter(Boolean);
    return [];
  };

  const resolveLinkedSources = async (refs) => {
    if (!selectedProject?.project_id || !refs.length) {
      setResolvedSourceRefs(refs);
      return;
    }

    setSourceRefsLoading(true);
    try {
      const response = await API_CONFIG.request(`/pkb/${selectedProject.project_id}/sources/resolve`, {
        method: 'POST',
        body: JSON.stringify({ source_refs: refs })
      });
      const json = await response.json();
      if (!response.ok || !json?.success) {
        throw new Error(json?.detail || 'Failed to resolve linked sources');
      }
      setResolvedSourceRefs(Array.isArray(json.sources) ? json.sources : refs);
    } catch (error) {
      setResolvedSourceRefs(refs.map((ref) => ({
        ...ref,
        availability_state: 'unknown',
        availability_label: 'Availability unknown',
        unavailable_message: 'Unable to verify source availability right now.'
      })));
      setSnackbarMsg(error.message || 'Failed to resolve linked sources');
      setSnackbarOpen(true);
    } finally {
      setSourceRefsLoading(false);
    }
  };

  const handleOpenSourcesModal = async (testCase, event) => {
    event?.stopPropagation?.();
    const refs = normalizeSourceRefs(testCase);
    if (!refs.length) return;
    setResolvedSourceRefs(refs);
    setSourceRefsDialogOpen(true);
    setSourceDetailDialogOpen(false);
    setSelectedSourceRef(null);
    setSourceDetail(null);
    await resolveLinkedSources(refs);
  };

  const handleCloseSourcesModal = () => {
    setSourceRefsDialogOpen(false);
    setResolvedSourceRefs([]);
    setSourceDetailDialogOpen(false);
    setSourceDetailLoading(false);
    setSelectedSourceRef(null);
    setSourceDetail(null);
  };

  const handleOpenSourceDetail = async (sourceRef) => {
    setSelectedSourceRef(sourceRef);
    setSourceDetailDialogOpen(true);

    if (sourceRef?.availability_state === 'unavailable') {
      setSourceDetailLoading(false);
      setSourceDetail({
        unavailable: true,
        message: sourceRef.unavailable_message || 'Associated source is not available anymore'
      });
      return;
    }

    if (!selectedProject?.project_id || !sourceRef?.pkb_entry_id) {
      setSourceDetailLoading(false);
      setSourceDetail({
        unavailable: true,
        message: 'Associated source is not available anymore'
      });
      return;
    }

    setSourceDetailLoading(true);
    setSourceDetail(null);
    try {
      const response = await API_CONFIG.request(`/pkb/${selectedProject.project_id}/document/${sourceRef.pkb_entry_id}`, {
        method: 'GET'
      });
      const json = await response.json();
      if (response.status === 404) {
        setSourceDetail({
          unavailable: true,
          message: 'Associated source is not available anymore'
        });
        return;
      }
      if (!response.ok || !json?.success) {
        throw new Error(json?.detail || 'Failed to load source details');
      }
      setSourceDetail({
        title: formatDocumentText(json.title || sourceRef.title || 'Source'),
        markdown: formatDocumentText(json.markdown || ''),
        sections: json.sections || {},
        inferredSections: json.inferred_sections || {},
        hasFigma: Boolean(json.has_figma),
        imagesCount: json.images_count || 0
      });
    } catch (error) {
      setSourceDetail({
        unavailable: true,
        message: error.message || 'Failed to load source details'
      });
    } finally {
      setSourceDetailLoading(false);
    }
  };

  // AI Logging: Component initialization
  useEffect(() => {
    aiLogger.component('TestCasesTable', 'Component Mounted', {
      testCasesCount: testCases.length,
      initialFilters: filters,
      initialSortConfig: sortConfig
    });
  }, [testCases.length, filters, sortConfig]);

  // AI Logging: Test cases data changes
  useEffect(() => {
    aiLogger.debug('TestCasesTable received test cases:', testCases);
    aiLogger.debug('Test case names:', testCases.map(tc => tc.title || tc.name));
    
    aiLogger.component('TestCasesTable', 'Test Cases Data Updated', {
      testCasesCount: testCases.length,
      testCaseNames: testCases.map(tc => tc.title || tc.name).slice(0, 5), // First 5 for context
      hasTestCases: testCases.length > 0
    });
  }, [testCases]);

  // Get unique values for filter options
  const filterOptions = useMemo(() => {
    if (mode === 'scenarios') {
      return { priorities: [], modules: [], testTypes: [], tags: [] };
    }
    const priorities = [...new Set(testCases.map(tc => tc.priority))].filter(Boolean);
    const modules = [...new Set(testCases.map(tc => tc.module || 'General'))].filter(Boolean);
    const testTypes = [...new Set(testCases.map(tc => tc.testType || 'Functional'))].filter(Boolean);
    const tags = [...new Set(testCases.map(tc => tc.sheet_name))].filter(Boolean).sort();
    aiLogger.debug('Filter Options Generated', { priorities, modules, testTypes, tags });
    return { priorities, modules, testTypes, tags };
  }, [testCases, mode]);

  // Apply filters and search
  const filteredTestCases = useMemo(() => {
    const measureDuration = aiLogger.measurePerformance('filtering');
    
    const filtered = testCases.filter(testCase => {
      const testCaseName = testCase.title || testCase.name;
      const matchesSearch =
        (testCaseName || '')?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (testCase.description || testCase.objective || '')?.toLowerCase().includes(searchTerm.toLowerCase());

      if (mode === 'scenarios') {
        return matchesSearch;
      }

      const matchesPriority = !filters.priority || testCase.priority === filters.priority;
      const matchesStatus = !filters.status || testCase.status === filters.status;
      const matchesModule = !filters.module || testCase.module === filters.module;
      const matchesTestType = !filters.testType || testCase.testType === filters.testType;
      const matchesTags = !filters.tags || testCase.sheet_name === filters.tags;
      const latestRunId = sessionStorage.getItem('test_cases_latest_run_id');
      const testCaseRunId = testCase.generation_run_id || testCase.meta?.generation_run_id || null;
      const matchesRun = runFilter !== 'latest' || !latestRunId || testCaseRunId === latestRunId;

      return matchesSearch && matchesPriority && matchesStatus && matchesModule && matchesTestType && matchesTags && matchesRun;
    });

    const duration = measureDuration();
    aiLogger.performance('Test Cases Filtering', duration, {
      originalCount: testCases.length,
      filteredCount: filtered.length,
      searchTerm,
      activeFilters: Object.entries(filters).filter(([_, value]) => value).length
    });

    return filtered;
  }, [testCases, searchTerm, filters, mode, runFilter]);

  // Apply sorting
  const sortedTestCases = useMemo(() => {
    const measureDuration = aiLogger.measurePerformance('sorting');
    
    const sorted = [...filteredTestCases].sort((a, b) => {
      let aValue, bValue;
      
      if (sortConfig.key === 'name') {
        aValue = a.title || a.name || '';
        bValue = b.title || b.name || '';
      } else if (sortConfig.key === 'source') {
        aValue = getTestCaseSourceLabel(a.source || a.source_type);
        bValue = getTestCaseSourceLabel(b.source || b.source_type);
      } else if (sortConfig.key === 'created_at') {
        aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
        bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
      } else {
        aValue = a[sortConfig.key] || '';
        bValue = b[sortConfig.key] || '';
      }
      
      if (sortConfig.key === 'created_at') {
        // For dates, compare numbers directly
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      } else {
        // For strings, use localeCompare with numeric/case-insensitive sorting
        const aText = String(aValue ?? '');
        const bText = String(bValue ?? '');
        if (sortConfig.direction === 'asc') {
          return aText.localeCompare(bText, undefined, { numeric: true, sensitivity: 'base' });
        } else {
          return bText.localeCompare(aText, undefined, { numeric: true, sensitivity: 'base' });
        }
      }
    });

    const duration = measureDuration();
    aiLogger.performance('Test Cases Sorting', duration, {
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction,
      itemsCount: sorted.length
    });

    return sorted;
  }, [filteredTestCases, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedTestCases.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTestCases = sortedTestCases.slice(startIndex, startIndex + itemsPerPage);

  // Sorting handler
  const handleSort = (key) => {
    const previousSort = { ...sortConfig };
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));

    aiLogger.userInteraction('sort_column', null, {
      column: key,
      previousSort,
      newSort: { key, direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc' }
    });
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    const previousSelection = [...selectedItems];
    const pageNames = paginatedTestCases.map(tc => tc.title || tc.name);
    if (checked) {
      setSelectedItems(pageNames);
    } else {
      setSelectedItems([]);
    }
    aiLogger.userInteraction('select_all_page', null, {
      checked,
      previousSelectionCount: previousSelection.length,
      newSelectionCount: checked ? pageNames.length : 0,
      affectedItems: checked ? pageNames : []
    });
  };

  const handleSelectAllAcrossPages = (checked) => {
    if (!checked) {
      setSelectedItems([]);
      return;
    }
    const allNames = sortedTestCases.map(tc => tc.title || tc.name);
    setSelectedItems(allNames);
    aiLogger.userInteraction('select_all_across_pages', null, {
      checked: true,
      totalSelected: allNames.length
    });
    setSnackbarMsg(`Selected all ${allNames.length} results across pages`);
    setSnackbarOpen(true);
  };

  const handleSelectItem = (testCaseName, checked) => {
    const previousSelection = [...selectedItems];
    
    if (checked) {
      setSelectedItems(prev => [...prev, testCaseName]);
    } else {
      setSelectedItems(prev => prev.filter(name => name !== testCaseName));
    }

    aiLogger.userInteraction('select_item', null, {
      testCaseName,
      checked,
      previousSelectionCount: previousSelection.length,
      newSelectionCount: checked ? previousSelection.length + 1 : previousSelection.length - 1
    });
  };

  // Bulk actions
  const handleBulkAction = async (action) => {
    aiLogger.debug(`${action} for:`, selectedItems);
    
    if (selectedItems.length === 0) {
      alert('Please select test cases first');
      return;
    }

    aiLogger.component('TestCasesTable', 'Bulk Action Executed', {
      action,
      selectedItems,
      selectedCount: selectedItems.length
    });

    if (action === 'delete' && onDeleteSelected) {
      setBulkDeleting(true);
      try {
        const result = await onDeleteSelected(selectedItems);
        if (result?.success) {
          // Clear selection only after delete + refresh confirmed
          setSelectedItems([]);
          setSnackbarMsg(result.message || `Deleted ${result.deletedCount} test case(s)`);
        } else {
          // Keep selection so the user can retry; rows stay visible
          setSnackbarMsg(result?.message || 'Failed to delete selected test cases');
        }
        setSnackbarOpen(true);
      } catch (err) {
        setSnackbarMsg('Failed to delete selected test cases');
        setSnackbarOpen(true);
        aiLogger.error('Bulk delete failed', err);
      } finally {
        setBulkDeleting(false);
      }
      return;
    }

    if (action === 'approve' && onApproveSelected) {
      onApproveSelected(selectedItems);
      setSelectedItems([]);
    }
  };



  const clearFilters = () => {
    const previousFilters = { ...filters };
    const previousSearch = searchTerm;
    
    setFilters({
      priority: '',
      status: '',
      module: '',
      testType: '',
      tags: ''
    });
    setSearchTerm('');

    aiLogger.userInteraction('clear_filters', null, {
      previousFilters,
      previousSearch,
      clearedAt: new Date().toISOString()
    });
  };

  const activeFiltersCount = Object.values(filters).filter(f => f).length + (searchTerm ? 1 : 0);
  const allVisibleRowsSelected =
    paginatedTestCases.length > 0 &&
    paginatedTestCases.every(tc => selectedItems.includes(tc.title || tc.name));
  const allResultsSelected = sortedTestCases.length > 0 && selectedItems.length === sortedTestCases.length;
  const canSelectAllResults = selectedItems.length > 0 && !allResultsSelected && sortedTestCases.length > 0;
  const latestRunId = sessionStorage.getItem('test_cases_latest_run_id');
  const hasLatestRunFilter = Boolean(latestRunId);

  useEffect(() => {
    if (hasLatestRunFilter) {
      setRunFilter('latest');
    }
  }, [hasLatestRunFilter]);

  // Error handling for empty state
  if (testCases.length === 0) {
    aiLogger.warn('TestCasesTable: No Test Cases Available', {
      component: 'TestCasesTable',
      expectedData: 'testCases prop'
    });

    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Box sx={{ fontSize: '4rem', mb: 2 }}>🧪</Box>
        <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
          No Test Cases Found
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Get started by creating your first test case or uploading from files.
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => {
              if (onOpenUpload) {
                onOpenUpload();
              } else {
                console.log('Upload test cases - handler not available');
              }
            }}
            sx={{ minWidth: 180 }}
          >
            Upload Test Cases
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<AutoAwesomeIcon />}
            onClick={() => {
              // TODO: Implement Universal Learning Explorer integration
              console.log('Run Universal Learning Explorer');
            }}
            sx={{ minWidth: 200 }}
          >
            Auto-Generate with Explorer
          </Button>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, opacity: 0.8 }}>
          💡 Tip: Upload Excel/CSV files for bulk test cases, or use the Explorer for automated discovery
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" component="h1" sx={{ mb: 0.5, fontWeight: 600, color: 'text.primary' }}>
              Test Cases
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Review and manage test cases with full lifecycle control. Approve, edit, or deprecate as needed.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, pr: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => {
                if (onOpenUpload) {
                  onOpenUpload();
                } else {
                  console.log('Upload test cases - handler not available');
                }
              }}
              size="small"
              sx={{ mt: 1 }}
            >
              Upload Test Cases
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                if (onShowLastImport) {
                  onShowLastImport();
                }
              }}
              size="small"
              sx={{ mt: 1 }}
            >
              Last Import Report
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Header with Search and Actions */}
      <Paper sx={{ p: 3, mb: 3 }}>

        {/* Active Filter Chips */}
        {activeFiltersCount > 0 && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              Active filters:
            </Typography>
            {filters.priority && (
              <Chip
                label={`Priority: ${filters.priority}`}
                onDelete={() => setFilters(prev => ({ ...prev, priority: '' }))}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {filters.status && (
              <Chip
                label={`Status: ${filters.status}`}
                onDelete={() => setFilters(prev => ({ ...prev, status: '' }))}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {filters.module && (
              <Chip
                label={`Module: ${filters.module}`}
                onDelete={() => setFilters(prev => ({ ...prev, module: '' }))}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {filters.testType && (
              <Chip
                label={`Type: ${filters.testType}`}
                onDelete={() => setFilters(prev => ({ ...prev, testType: '' }))}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {filters.sheetName && (
              <Chip
                label={`Sheet: ${filters.sheetName}`}
                onDelete={() => setFilters(prev => ({ ...prev, sheetName: '' }))}
                size="small"
                color="secondary"
                variant="outlined"
              />
            )}
          </Box>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <TextField
              placeholder={mode === 'scenarios' ? 'Search scenarios...' : 'Search test cases...'}
              value={searchTerm}
              onChange={(e) => {
                const newValue = e.target.value;
                setSearchTerm(newValue);
                aiLogger.userInteraction('search_input', e.target, {
                  searchTerm: newValue,
                  previousSearch: searchTerm
                });
              }}
              inputProps={{ 'data-test': mode === 'scenarios' ? 'scenarios-search' : 'tests-search' }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                endAdornment: searchTerm ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchTerm('');
                      aiLogger.userInteraction('clear_search', null, {
                        previousSearch: searchTerm
                      });
                    }}
                    sx={{ 
                      mr: 0.5,
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                    title="Clear search"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : null
              }}
              sx={{ minWidth: 300 }}
              size="small"
            />
            
            {mode !== 'scenarios' && (
            <Button
              variant={showFilters ? "contained" : "outlined"}
              startIcon={<FilterIcon />}
              onClick={() => {
                setShowFilters(!showFilters);
                aiLogger.userInteraction('toggle_filters', null, {
                  newState: !showFilters,
                  activeFiltersCount
                });
              }}
              sx={{ 
                minWidth: 'auto',
                px: 2,
                ...(activeFiltersCount > 0 && {
                  '&::after': {
                    content: `"${activeFiltersCount}"`,
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: 'error.main',
                    color: 'error.contrastText',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    fontSize: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }
                })
              }}
            >
              Filters
            </Button>)}
            {hasLatestRunFilter && (
              <Button
                variant={runFilter === 'latest' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setRunFilter(prev => (prev === 'latest' ? 'all' : 'latest'))}
              >
                {runFilter === 'latest' ? 'Latest run' : 'All runs'}
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedItems.length > 0 && mode !== 'scenarios' && (
              <>
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/tests/${selectedProject?.project_id || 1}/test-cases/download?format=excel&selected=${selectedItems.join(',')}`;
                    window.open(url, '_blank');
                    aiLogger.userInteraction('export_selected_test_cases', null, {
                      format: 'excel',
                      projectId: selectedProject?.project_id || 1,
                      selectedCount: selectedItems.length
                    });
                  }}
                  variant="outlined"
                  color="success"
                >
                  Export
                </Button>
                {onApproveSelected && (
                  <Button
                    size="small"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() => handleBulkAction('approve')}
                    variant="outlined"
                    color="success"
                  >
                    Approve
                  </Button>
                )}
                <Button
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleBulkAction('delete')}
                  variant="outlined"
                  color="error"
                  disabled={bulkDeleting}
                >
                  {bulkDeleting ? 'Deleting…' : 'Delete'}
                </Button>
              </>
            )}
            {onRefresh && (
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  onRefresh();
                  aiLogger.userInteraction('refresh_test_cases', null, {
                    component: 'TestCasesTable'
                  });
                }}
                size="small"
                sx={{ minWidth: 'auto', px: 2 }}
                data-test={mode === 'scenarios' ? 'scenarios-refresh' : 'tests-refresh'}
              >
                Refresh
              </Button>
            )}
          </Box>
        </Box>

        {/* Advanced Filters */}
        {showFilters && mode !== 'scenarios' && (
          <Box sx={{ 
            borderTop: 1, 
            borderColor: 'divider', 
            pt: 2,
            mt: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Filter by:
              </Typography>
              <Button
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                variant="outlined"
                size="small"
                color="error"
                disabled={activeFiltersCount === 0}
              >
                Clear All ({activeFiltersCount})
              </Button>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="medium" sx={{ minWidth: '120px' }}>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={filters.priority}
                    label="Priority"
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setFilters(prev => ({ ...prev, priority: newValue }));
                      aiLogger.userInteraction('filter_priority', e.target, {
                        value: newValue,
                        previousValue: filters.priority
                      });
                    }}
                  >
                    <MenuItem value="">All Priorities</MenuItem>
                    {filterOptions.priorities.map(p => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="medium" sx={{ minWidth: '120px' }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setFilters(prev => ({ ...prev, status: newValue }));
                      aiLogger.userInteraction('filter_status', e.target, {
                        value: newValue,
                        previousValue: filters.status
                      });
                    }}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="approval_pending">⏳ Approval Pending</MenuItem>
                    <MenuItem value="approved">✅ Approved</MenuItem>
                    <MenuItem value="regression">🔄 Regression</MenuItem>
                    <MenuItem value="deprecated">🗑️ Deprecated</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="medium" sx={{ minWidth: '120px' }}>
                  <InputLabel>Module</InputLabel>
                  <Select
                    value={filters.module}
                    label="Module"
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setFilters(prev => ({ ...prev, module: newValue }));
                      aiLogger.userInteraction('filter_module', e.target, {
                        value: newValue,
                        previousValue: filters.module
                      });
                    }}
                  >
                    <MenuItem value="">All Modules</MenuItem>
                    {filterOptions.modules.map(m => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth size="medium" sx={{ minWidth: '120px' }}>
                  <InputLabel>Test Type</InputLabel>
                  <Select
                    value={filters.testType}
                    label="Test Type"
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setFilters(prev => ({ ...prev, testType: newValue }));
                      aiLogger.userInteraction('filter_testType', e.target, {
                        value: newValue,
                        previousValue: filters.testType
                      });
                    }}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {filterOptions.testTypes.map(t => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth size="medium" sx={{ minWidth: '120px' }}>
                  <InputLabel>Tags</InputLabel>
                  <Select
                    value={filters.tags}
                    label="Tags"
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setFilters(prev => ({ ...prev, tags: newValue }));
                      aiLogger.userInteraction('filter_tags', e.target, {
                        value: newValue,
                        previousValue: filters.tags
                      });
                    }}
                  >
                    <MenuItem value="">All Tags</MenuItem>
                    {filterOptions.tags.map(t => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

          </Box>
        )}
      </Paper>

      {/* Results Summary */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedTestCases.length)} of {sortedTestCases.length} {mode === 'scenarios' ? 'scenarios' : 'test cases'}
            {totalCount > 0 && totalCount !== sortedTestCases.length && (
              <span style={{ color: theme.palette.text.secondary, fontSize: '0.9em' }}> (Total in system: {totalCount})</span>
            )}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end', ml: 'auto' }}>
            {selectedItems.length > 0 && mode !== 'scenarios' && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {selectedItems.length} selected
                </Typography>
                {canSelectAllResults && (
                  <Button size="small" onClick={() => handleSelectAllAcrossPages(true)} sx={{ minWidth: 'auto', px: 0.5 }}>
                    Select all
                  </Button>
                )}
                <Button size="small" onClick={() => handleSelectAllAcrossPages(false)} sx={{ minWidth: 'auto', px: 0.5 }}>
                  Clear all
                </Button>
              </>
            )}

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={itemsPerPage}
                onChange={(e) => {
                  const newValue = Number(e.target.value);
                  setItemsPerPage(newValue);
                  setCurrentPage(1);
                  aiLogger.userInteraction('change_items_per_page', e.target, {
                    newValue,
                    previousValue: itemsPerPage
                  });
                }}
              >
                <MenuItem value={5}>5 per page</MenuItem>
                <MenuItem value={10}>10 per page</MenuItem>
                <MenuItem value={25}>25 per page</MenuItem>
                <MenuItem value={50}>50 per page</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      {/* Enhanced Table */}
      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {mode !== 'scenarios' && (
                  <TableCell padding="checkbox" sx={{ width: 56, minWidth: 56, maxWidth: 56 }}>
                    <Checkbox
                      checked={allVisibleRowsSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </TableCell>
                )}
                <TableCell
                  onClick={() => handleSort('name')}
                  sx={{ cursor: 'pointer', fontWeight: 600 }}
                >
                  {mode === 'scenarios' ? 'Title' : 'Test Case Name'}
                  {sortConfig.key === 'name' && (
                    <span style={{ marginLeft: 4 }}>
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                {mode !== 'scenarios' ? (
                  <>
                    <TableCell onClick={() => handleSort('priority')} sx={{ cursor: 'pointer', fontWeight: 600 }}>
                      Priority
                      {sortConfig.key === 'priority' && (<span style={{ marginLeft: 4 }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>)}
                    </TableCell>
                    <TableCell onClick={() => handleSort('status')} sx={{ cursor: 'pointer', fontWeight: 600 }}>
                      Status
                      {sortConfig.key === 'status' && (<span style={{ marginLeft: 4 }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>)}
                    </TableCell>
                    <TableCell onClick={() => handleSort('source')} sx={{ cursor: 'pointer', fontWeight: 600 }}>
                      Source
                      {sortConfig.key === 'source' && (<span style={{ marginLeft: 4 }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Story</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tags</TableCell>
                    <TableCell onClick={() => handleSort('created_at')} sx={{ cursor: 'pointer', fontWeight: 600 }}>
                      Created
                      {sortConfig.key === 'created_at' && (<span style={{ marginLeft: 4 }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </>
                                 ) : (
                   <>
                     <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                     <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                     <TableCell sx={{ fontWeight: 600 }}>Tags</TableCell>
                     <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                   </>
                 )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTestCases.map((testCase, index) => (
                <TableRow 
                  key={testCase.test_id || testCase.id || index} 
                >
                  {mode !== 'scenarios' && (
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedItems.includes(testCase.title || testCase.name)}
                        onChange={(e) => handleSelectItem(testCase.title || testCase.name, e.target.checked)}
                      />
                    </TableCell>
                  )}
                  <TableCell
                    data-test={`tests-row-${(testCase.title || testCase.name || '').toString().toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                  > 
                    <ButtonBase
                      onClick={() => {
                        setSelectedTestCase(testCase);
                        aiLogger.userInteraction('select_test_case', null, {
                          testCaseName: testCase.title || testCase.name,
                          testCaseIndex: index,
                          pageIndex: startIndex + index
                        });
                      }}
                      sx={{
                        width: '100%',
                        display: 'block',
                        textAlign: 'left',
                        borderRadius: 1,
                        px: 0.5,
                        py: 0.25,
                        '&:hover .test-case-title': { textDecoration: 'underline' }
                      }}
                    >
                      <Box>
                        <Typography
                          className="test-case-title"
                          variant="body1"
                          fontWeight={600}
                        >
                          {testCase.title || testCase.name}
                        </Typography>
                        {mode !== 'scenarios' ? (
                          <Typography variant="body2" color="text.secondary">{testCase.description}</Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">{testCase.description || testCase.objective}</Typography>
                        )}
                      </Box>
                    </ButtonBase>
                  </TableCell>
                  {mode !== 'scenarios' ? (
                    <>
                      <TableCell>
                        <Chip label={testCase.priority} size="small" color={testCase.priority === 'High' ? 'error' : testCase.priority === 'Medium' ? 'warning' : 'default'} />
                      </TableCell>
                      <TableCell data-test={`tests-status-cell-${(testCase.title || testCase.name || '').toString().toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                        {showApprovalWorkflow ? (
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select data-test={`tests-status-select-${(testCase.title || testCase.name || '').toString().toLowerCase().replace(/[^a-z0-9]+/g,'-')}`} value={(testCase.status || 'approval_pending').toLowerCase()} onChange={(e) => { e.stopPropagation(); if (onStatusChange) { onStatusChange(testCase.test_id || testCase.id, e.target.value); } aiLogger.userInteraction('change_test_case_status', e.target, { testCaseId: testCase.test_id || testCase.id, testCaseName: testCase.title || testCase.name, previousStatus: testCase.status, newStatus: e.target.value }); }} onClick={(e) => e.stopPropagation()}>
                              <MenuItem data-test="tests-status-option-approval_pending" value="approval_pending">⏳ Approval Pending</MenuItem>
                              <MenuItem data-test="tests-status-option-approved" value="approved">✅ Approved</MenuItem>
                              <MenuItem data-test="tests-status-option-rejected" value="rejected">❌ Rejected</MenuItem>
                              <MenuItem data-test="tests-status-option-draft" value="draft">📝 Draft</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select value={(testCase.status || 'approval_pending').toLowerCase()} onChange={(e) => { e.stopPropagation(); if (onStatusChange) { onStatusChange(testCase.test_id || testCase.id, e.target.value); } aiLogger.userInteraction('change_test_case_status', e.target, { testCaseId: testCase.test_id || testCase.id, testCaseName: testCase.title || testCase.name, previousStatus: testCase.status, newStatus: e.target.value }); }} onClick={(e) => e.stopPropagation()}>
                              <MenuItem value="approval_pending">⏳ Approval Pending</MenuItem>
                              <MenuItem value="approved">✅ Approved</MenuItem>
                              <MenuItem value="rejected">❌ Rejected</MenuItem>
                              <MenuItem value="draft">📝 Draft</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const sourceRefs = normalizeSourceRefs(testCase);
                          const hasLinkedSources = sourceRefs.length > 0;

                          if (!hasLinkedSources) {
                            return (
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                                <Chip
                                  label={getTestCaseSourceLabel(testCase.source || testCase.source_type)}
                                  size="small"
                                  color={getSourceColor(testCase.source || testCase.source_type)}
                                  variant="outlined"
                                />
                              </Box>
                            );
                          }

                          return (
                            <ButtonBase
                              onClick={(event) => handleOpenSourcesModal(testCase, event)}
                              sx={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                                borderRadius: 1,
                                px: 0.5,
                                py: 0.25
                              }}
                            >
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                                <Chip
                                  label={getTestCaseSourceLabel(testCase.source || testCase.source_type)}
                                  size="small"
                                  color={getSourceColor(testCase.source || testCase.source_type)}
                                  variant="outlined"
                                />
                              </Box>
                            </ButtonBase>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const storyLabel = getTestCaseStoryLabel(testCase);
                          return storyLabel ? (
                          <Chip 
                            label={storyLabel}
                            size="small" 
                            variant="outlined" 
                            color="primary"
                            sx={{ fontWeight: 500 }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        );
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Display modules if available, otherwise fall back to tags
                          const modules = testCase.modules || [];
                          const tags = testCase.tags || [];
                          const displayItems = modules.length > 0 ? modules : tags;
                          
                          if (displayItems.length > 0) {
                            return (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                {displayItems.slice(0, 3).map((item, idx) => {
                                  // Handle both string and object formats
                                  const moduleKey = typeof item === 'string' ? item : (item.module_key || item);
                                  const isPrimary = testCase.primary_module && (
                                    typeof testCase.primary_module === 'string'
                                      ? testCase.primary_module === moduleKey
                                      : testCase.primary_module.module_key === moduleKey
                                  );
                                  const isAutoGenerated = typeof item === 'object' && item.created_by === 'system';
                                  
                                  return (
                                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                      <Chip
                                        label={moduleKey}
                                        size="small"
                                        color={isPrimary ? "primary" : "default"}
                                        variant={isPrimary ? "filled" : "outlined"}
                                        sx={{ fontSize: '0.65rem', height: '18px' }}
                                      />
                                      {isAutoGenerated && (
                                        <Chip
                                          label="Auto"
                                          size="small"
                                          variant="outlined"
                                          sx={{
                                            fontSize: '0.55rem',
                                            height: '16px',
                                            color: 'text.secondary',
                                            borderColor: 'text.secondary',
                                            '& .MuiChip-label': { px: 0.5 }
                                          }}
                                          title="Auto-generated module"
                                        />
                                      )}
                                    </Box>
                                  );
                                })}
                                {displayItems.length > 3 && (
                                  <Typography variant="caption" color="text.secondary">
                                    +{displayItems.length - 3}
                                  </Typography>
                                )}
                              </Box>
                            );
                          }
                          return <Typography variant="body2" color="text.secondary">-</Typography>;
                        })()}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {testCase.created_at ? new Date(testCase.created_at).toLocaleDateString() + ' ' + new Date(testCase.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setSelectedTestCase(testCase); aiLogger.userInteraction('view_test_case_details', e.target, { testCaseName: testCase.title || testCase.name }); }}>
                          <ViewIcon />
                        </IconButton>
                      </TableCell>
                    </>
                                   ) : (
                   <>
                     <TableCell>
                       <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 480 }}>
                         {testCase.description || testCase.objective}
                       </Typography>
                     </TableCell>
                     <TableCell>
                       {showApprovalWorkflow ? (
                         <FormControl size="small" sx={{ minWidth: 120 }}>
                           <Select value={(testCase.status || 'approval_pending').toLowerCase()} onChange={(e) => { e.stopPropagation(); if (onStatusChange) { onStatusChange(testCase.test_id || testCase.id, e.target.value); } aiLogger.userInteraction('change_test_case_status', e.target, { testCaseId: testCase.test_id || testCase.id, testCaseName: testCase.title || testCase.name, previousStatus: testCase.status, newStatus: e.target.value }); }} onClick={(e) => e.stopPropagation()}>
                             <MenuItem value="approval_pending">⏳ Approval Pending</MenuItem>
                             <MenuItem value="approved">✅ Approved</MenuItem>
                             <MenuItem value="rejected">❌ Rejected</MenuItem>
                             <MenuItem value="draft">📝 Draft</MenuItem>
                           </Select>
                         </FormControl>
                       ) : (
                         <span className={`status-badge ${testCase.status || 'approval_pending'}`}>
                           {testCase.status === 'approved' ? '✅ Approved' : 
                            testCase.status === 'rejected' ? '❌ Rejected' : 
                            '⏳ Pending'}
                         </span>
                       )}
                     </TableCell>
                     <TableCell>
                       {(testCase.tags || []).slice(0, 3).map((t, i) => (
                         <Chip key={i} label={t} size="small" sx={{ mr: 0.5 }} />
                       ))}
                     </TableCell>
                     <TableCell>{(testCase.created_at || '').split('T')[0] || '-'}</TableCell>
                   </>
                 )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination */}
      {totalPages > 1 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Button 
              onClick={() => {
                const newPage = Math.max(1, currentPage - 1);
                setCurrentPage(newPage);
                aiLogger.userInteraction('pagination_previous', null, {
                  previousPage: currentPage,
                  newPage
                });
              }}
              disabled={currentPage === 1}
              variant="outlined"
              size="small"
            >
              ← Previous
            </Button>
            
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <Button
                  key={page}
                  onClick={() => {
                    setCurrentPage(page);
                    aiLogger.userInteraction('pagination_page', null, {
                      previousPage: currentPage,
                      newPage: page
                    });
                  }}
                  variant={currentPage === page ? 'contained' : 'outlined'}
                  size="small"
                  sx={{ minWidth: 40 }}
                >
                  {page}
                </Button>
              ))}
            </Box>
            
            <Button 
              onClick={() => {
                const newPage = Math.min(totalPages, currentPage + 1);
                setCurrentPage(newPage);
                aiLogger.userInteraction('pagination_next', null, {
                  previousPage: currentPage,
                  newPage
                });
              }}
              disabled={currentPage === totalPages}
              variant="outlined"
              size="small"
            >
              Next →
            </Button>
          </Box>
        </Paper>
      )}

      {/* Enhanced Modal */}
      {selectedTestCase && mode !== 'scenarios' && (
        <Dialog
          open={!!selectedTestCase}
          onClose={() => {
            setSelectedTestCase(null);
            aiLogger.userInteraction('close_modal', null, {
              testCaseName: selectedTestCase.title || selectedTestCase.name
            });
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">
                {selectedTestCase.title || selectedTestCase.name}
              </Typography>
              <IconButton
                onClick={() => {
                  setSelectedTestCase(null);
                  aiLogger.userInteraction('close_modal_button', null, {
                    testCaseName: selectedTestCase.title || selectedTestCase.name
                  });
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          
          <DialogContent>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Description</Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {selectedTestCase.description}
              </Typography>
            </Box>
            
            {/* Show detailed steps and expected results */}
            {selectedTestCase.steps && selectedTestCase.steps.length > 0 ? (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Test Steps</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {selectedTestCase.steps.map((step, index) => (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      p: 2, 
                      border: '1px solid #e0e0e0', 
                      borderRadius: 1,
                      backgroundColor: '#f8f9fa'
                    }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 'bold', 
                        minWidth: '40px',
                        color: '#1976d2'
                      }}>
                        {step.step || (index + 1)}
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 'medium', 
                          mb: 0.5,
                          color: '#2c3e50'
                        }}>
                          {step.action}
                        </Typography>
                        {step.target && (
                          <Typography variant="caption" sx={{ 
                            color: '#34495e',
                            display: 'block',
                            mt: 0.5
                          }}>
                            Target: {step.target}
                          </Typography>
                        )}
                        {step.value && (
                          <Typography variant="caption" sx={{ 
                            color: '#34495e',
                            display: 'block',
                            mt: 0.5
                          }}>
                            Value: {step.value}
                          </Typography>
                        )}
                        {step.description && step.description !== step.action && (
                          <Typography variant="body2" sx={{ 
                            color: '#34495e',
                            mt: 0.5,
                            fontSize: '0.875rem',
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'anywhere'
                          }}>
                            {step.description}
                          </Typography>
                        )}
                        {step.expected_result && (
                          <Typography variant="body2" sx={{ 
                            color: '#2c3e50',
                            mt: 0.5,
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'anywhere'
                          }}>
                            Expected: {step.expected_result}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : selectedTestCase.meta && selectedTestCase.meta.steps && selectedTestCase.meta.steps.length > 0 ? (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Test Steps</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {selectedTestCase.meta.steps.map((step, index) => (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      p: 2, 
                      border: '1px solid #e0e0e0', 
                      borderRadius: 1,
                      backgroundColor: '#f8f9fa'
                    }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 'bold', 
                        minWidth: '40px',
                        color: '#1976d2'
                      }}>
                        {step.step_number || (index + 1)}
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 'medium', 
                          mb: 0.5,
                          color: '#2c3e50'
                        }}>
                          {step.action}
                        </Typography>
                        {step.expected_result && (
                          <Typography variant="body2" sx={{ 
                            color: '#2c3e50',
                            mt: 0.5,
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'anywhere'
                          }}>
                            Expected: {step.expected_result}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : null}

            {selectedTestCase.expected_results && selectedTestCase.expected_results.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Expected Results</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {selectedTestCase.expected_results.map((result, index) => (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      gap: 1, 
                      p: 1.5, 
                      border: '1px solid #e8f5e8', 
                      borderRadius: 1,
                      backgroundColor: '#f8fff8'
                    }}>
                      <Typography variant="body2" sx={{ color: '#2e7d32' }}>✓</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{result}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {selectedTestCase.preconditions && selectedTestCase.preconditions.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Preconditions</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {selectedTestCase.preconditions.map((precondition, index) => (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      gap: 1, 
                      p: 1.5, 
                      border: '1px solid #fff3e0', 
                      borderRadius: 1,
                      backgroundColor: '#fffbf0'
                    }}>
                      <Typography variant="body2" sx={{ color: '#f57c00' }}>⚡</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{precondition}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {selectedTestCase.tags && selectedTestCase.tags.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>Tags</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {selectedTestCase.tags.map((tag, index) => (
                    <Chip key={index} label={`#${tag}`} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}
          </DialogContent>
          
                     <DialogActions>
            <Tooltip title="Coming soon" placement="top">
              <span>
                <Button variant="outlined" startIcon={<EditIcon />} disabled>
                  Edit
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Coming soon" placement="top">
              <span>
                <Button variant="outlined" startIcon={<DuplicateIcon />} disabled>
                  Duplicate
                </Button>
              </span>
            </Tooltip>
          </DialogActions>
        </Dialog>
      )}
      {selectedTestCase && mode === 'scenarios' && (
        <Dialog open={!!selectedTestCase} onClose={() => setSelectedTestCase(null)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">{selectedTestCase.title}</Typography>
              <IconButton onClick={() => setSelectedTestCase(null)}><CloseIcon /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Objective</Typography>
              <Typography variant="body1">{selectedTestCase.objective || selectedTestCase.description}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Description</Typography>
              <Typography variant="body2" color="text.secondary">{selectedTestCase.description || selectedTestCase.objective}</Typography>
            </Box>
            {(selectedTestCase.tags || []).length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>Tags</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {selectedTestCase.tags.map((tag, idx) => (<Chip key={idx} label={`#${tag}`} size="small" variant="outlined" />))}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Tooltip title="Coming soon"><span><Button variant="outlined" startIcon={<EditIcon />} disabled>Edit</Button></span></Tooltip>
            <Tooltip title="Coming soon"><span><Button variant="outlined" startIcon={<DuplicateIcon />} disabled>Duplicate</Button></span></Tooltip>
          </DialogActions>
        </Dialog>
      )}
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity="info" sx={{ width: '100%' }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
      <Dialog open={sourceRefsDialogOpen} onClose={handleCloseSourcesModal} maxWidth="sm" fullWidth>
        <DialogTitle>Linked sources</DialogTitle>
        <DialogContent dividers>
          {sourceRefsLoading ? (
            <Box sx={{ py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Checking linked source availability...
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {resolvedSourceRefs.map((ref, idx) => (
                <ListItemButton
                  key={`${ref.pkb_entry_id || ref.jira_key || idx}`}
                  onClick={() => handleOpenSourceDetail(ref)}
                  sx={{ alignItems: 'flex-start', py: 1.5 }}
                >
                  <ListItemText
                    primary={ref.title || ref.pkb_entry_id || `Source ${idx + 1}`}
                    secondary={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                        {ref.jira_key && (
                          <Typography variant="body2" color="text.secondary">
                            {ref.jira_key}
                          </Typography>
                        )}
                        <Typography variant="caption" color={ref.availability_state === 'available' ? 'success.main' : 'text.secondary'}>
                          {ref.availability_label || (ref.availability_state === 'available' ? 'Available' : 'Source no longer available')}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSourcesModal}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={sourceDetailDialogOpen}
        onClose={() => {
          setSourceDetailDialogOpen(false);
          setSourceDetail(null);
          setSelectedSourceRef(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedSourceRef?.title || 'Source detail'}</DialogTitle>
        <DialogContent dividers>
          {sourceDetailLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading source details...
            </Typography>
          ) : sourceDetail?.unavailable ? (
            <Alert severity="warning">
              {sourceDetail.message || 'Associated source is not available anymore'}
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedSourceRef?.jira_key && (
                  <Chip label={selectedSourceRef.jira_key} size="small" color="primary" variant="outlined" />
                )}
                {sourceDetail?.hasFigma && (
                  <Chip label="Figma linked" size="small" color="secondary" variant="outlined" />
                )}
              </Box>

              {Object.entries(sourceDetail?.sections || {}).map(([sectionKey, sectionValue]) => (
                <Box key={sectionKey}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {formatSectionLabel(sectionKey)}
                  </Typography>
                  {Array.isArray(sectionValue) ? (
                    <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                      {sectionValue.map((item, idx) => (
                        <Box component="li" key={`${sectionKey}-${idx}`} sx={{ color: 'text.secondary', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                            {formatDocumentText(item)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {formatDocumentText(sectionValue)}
                    </Typography>
                  )}
                </Box>
              ))}

              {sourceDetail?.markdown && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Description
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    {sourceDetail.markdown}
                  </Typography>
                </Box>
              )}

              {!sourceDetail?.markdown && !Object.keys(sourceDetail?.sections || {}).length && (
                <Typography variant="body2" color="text.secondary">
                  No additional source details are available.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSourceDetailDialogOpen(false);
              setSourceDetail(null);
              setSelectedSourceRef(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestCasesTable; 